import { t as tr } from '@hana/i18n';
import './CostPhone.i18n';
import { useState } from 'react';
import { IconAlertTriangle, IconChevronLeft, IconClockHour4, IconLock, IconPhoto, IconPlus, IconReceipt2 } from '@tabler/icons-react';
import { Page, PageHeader } from '@hana/shell';
import { Button, Segmented, StateView, useToast, type ScreenState } from '@hana/ui';
import { useT } from '@hana/i18n';
import './roePhone.i18n';
import { financeScreenState, useFinanceAccess } from './access';
import { PHONE_ITEMS, chipKind, chipTip, phoneCounts, type PhoneStatus } from './costPhoneData';
import s from './costPhone.module.css';

// Mint-Cost-Phone: khung 1 = camera chụp chứng từ · khung 2 = "Phiếu chi của tôi" (Hôm nay / Bị trả về). Mọi số là TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.

type Tab = 'today' | 'ret';
const ST_CLS: Record<PhoneStatus, string> = { Created: 'stPri', Approved: 'stOk', 'Nháp': 'stWarn', 'Need Revise': 'stErr' };
const STATE_MSG: Partial<Record<ScreenState, { title: string; desc: string }>> = {
  empty: { title: 'Chưa có khoản chi', desc: 'Hôm nay chưa có khoản chi nào. Bấm + Chụp để nhập.' },
  error: { title: 'Mất kết nối', desc: 'Ảnh đã chụp vẫn lưu trên máy, sẽ tự gửi khi có mạng.' },
  noperm: { title: 'Chưa có quyền', desc: 'Tài khoản chưa được cấp quyền nhập chi phí hiện trường.' },
};

function Camera({ onBack }: { onBack: () => void }) {
  const toast = useToast();
  const tx = useT();
  const noWrite = () => toast({ msg: tx('fnp.noWrite') });
  return (
    <div className={s.cam}>
      <div className={s.camTop}>
        <button type="button" className={s.camBtn} aria-label={tx('fnp.back')} onClick={onBack}>
          <IconChevronLeft size={22} aria-hidden="true" />
        </button>
        <b>{tx('fnp.cap')}</b>
        <span className={s.camGap} />
      </div>
      <div className={s.view}>
        <span className={s.hint}>{tx('fnp.hint')}</span>
      </div>
      <div className={s.camBar}>
        <button type="button" className={s.camSide} onClick={noWrite}>{tx('fnp.blur')}</button>
        <button type="button" className={s.shutter} aria-label={tx('fnp.shoot')} onClick={noWrite} />
        <button type="button" className={s.camSide} onClick={noWrite}>
          <IconPhoto size={16} aria-hidden="true" /> {tx('fnp.lib')}
        </button>
      </div>
    </div>
  );
}

export function CostPhone() {
  const tx = useT();
  const toast = useToast();
  const access = useFinanceAccess();
  const [tab, setTab] = useState<Tab>('today');
  const [cam, setCam] = useState(false);
  const state = financeScreenState(access);
  const ready = state === 'ready';
  const n = phoneCounts();
  const rows = PHONE_ITEMS.filter((x) => (tab === 'ret') === x.returned);

  if (cam && ready) return <Camera onBack={() => setCam(false)} />;

  return (
    <Page kind="table">
      <PageHeader
        title="Phiếu chi của tôi"
        actions={
          <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} disabled={!ready} onClick={() => setCam(true)}>
            {tr('cph.1')}
          </Button>
        }
      />
      <div className={s.page}>
        <Segmented
          label="Lọc"
          value={tab}
          onChange={setTab}
          items={[
            { key: 'today', label: 'Hôm nay', n: n.today },
            { key: 'ret', label: 'Bị trả về', n: n.ret },
          ]}
        />
        {state === 'loading' && <StateView state="loading" />}
        {state !== 'loading' && !ready && STATE_MSG[state] && (
          <StateView
            state={state}
            icon={state === 'noperm' ? <IconLock size={28} aria-hidden="true" /> : undefined}
            title={STATE_MSG[state]!.title}
            desc={STATE_MSG[state]!.desc}
          />
        )}
        {ready && (
          <>
            <div className={s.warn}>
              <span title={tx('fnp.dueToday')} aria-label={tx('fnp.dueToday')}><IconClockHour4 size={14} aria-hidden="true" /> <b>{tx('fnp.today')}</b></span>
              <span title={tx('fnp.unsent')} aria-label={tx('fnp.unsent')}><IconAlertTriangle size={14} aria-hidden="true" /> 1</span>
            </div>
            <div className={s.kpi}>
              <div><span>{tx('fnp.today')}</span><b>{tx('fnp.nItems', { n: 5 })}</b></div>
              <div><span>{tx('fnp.okPhoto')}</span><b className={s.ok}>91%</b></div>
              <div><span>{tx('fnp.returned')}</span><b className={n.ret ? s.err : undefined}>{n.ret}</b></div>
            </div>
            <ul className={s.list}>
              {rows.map((x) => (
                <li key={x.title}>
                  <button type="button" className={s.row} onClick={() => toast({ msg: x.why })}>
                    <span className={s.sq} aria-hidden="true"><IconReceipt2 size={18} /></span>
                    <span className={s.tx}>
                      <span className={s.name}>{x.title}</span>
                      <span className={s.l2}>
                        {x.job}
                        {x.chips.map((c, i) => (
                          <span key={c} className={`${s.chip} ${s[chipKind(c, i)]}`} title={chipTip(c)} aria-label={chipTip(c)}>{c}</span>
                        ))}
                      </span>
                    </span>
                    <span className={s.end}>
                      <b>{x.amt}</b>
                      <span className={`${s.st} ${s[ST_CLS[x.returned ? 'Need Revise' : x.status]]}`}>{x.returned ? 'Need Revise' : x.status}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Page>
  );
}
